/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { observer } from 'mobx-react';
import React from 'react';
import { Redirect, Switch, useHistory } from 'react-router';
import { NotFoundRedirect } from 'src/core/components/NotFoundRoute';
import { Page } from 'src/core/components/Page';
import { PrivateRoute } from 'src/core/components/PrivateRoute';
import { ShimmerFactory } from 'src/core/components/ShimmerFactory';
import { createAzureMachineLearningHandlerGroup } from 'src/core/handlers/azure/azureMachineLearningHandlerGroup';
import { createAzureOpenAiHandlerGroup } from 'src/core/handlers/azureOpenAiHandlerGroup';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useInstallHandlerGroup } from 'src/core/hooks/useInstallHandlerGroup';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { globalMessages } from 'src/core/utils/globalMessages';
import { routeTable } from 'src/core/utils/routeTable';
import { CustomTextAutoTaggingPage } from 'src/customText/appShell/autoTaggingPage/CustomTextAutoTaggingPage';
import { CustomTextSettingsPage } from 'src/customText/appShell/CustomTextSettingsPage';
import { CustomTextDataTaggingPage } from 'src/customText/appShell/dataTaggingPage/CustomTextDataTaggingPage';
import { CustomTextModelDetailsListPage } from 'src/customText/appShell/modelDetailsPages/CustomTextModelDetailsListPage';
import { CustomTextModelDetailsPage } from 'src/customText/appShell/modelDetailsPages/CustomTextModelDetailsPage';
import { CustomTextTestModelPage } from 'src/customText/appShell/testModelPage/CustomTextTestModelPage';
import { useAutoTaggingJobTracker } from 'src/customText/core/hooks/useAutoTaggingJobTracker';
import { useInitializeCustomTextProject } from 'src/customText/core/hooks/useInitializeCustomTextProject';
import { CustomTextProjectKind } from 'src/customText/core/stores/CustomTextProjectStore';
import { createCustomTextAutoTaggingHandlerGroup } from 'src/customText/modules/handlers/customTextAutoTaggingHandlerGroup/customTextAutoTaggingHandlerGroup';
import { createCustomTextDeployModelHandlerGroup } from 'src/customText/modules/handlers/customTextDeployModelHandlerGroup';
import { createCustomTextGptHandlerGroup } from 'src/customText/modules/handlers/customTextGptHandlerGroup/customTextGptHandlerGroup';
import { createCustomTextTrainingHandlerGroup } from 'src/customText/modules/handlers/customTextTrainingHandlerGroup/customTextTrainingHandlerGroup';
import { useModelEvaluationTracker } from 'src/modules/modelEvaluation/hooks/useModelEvaluationTracker';
import { useModelTrainingTracker } from 'src/modules/modelTraining/hooks/useModelTrainingTracker';
import { useModelTrainingUpdate } from 'src/modules/modelTraining/hooks/useModelTrainingUpdate';
import { CustomTextDeployModelPage } from 'src/verticals/customText/CustomTextDeployModelPage';
import { CustomTextTrainingPage } from 'src/verticals/customText/CustomTextTrainingPage';

type ProjectPageProps = { projectId: string; projectKind: CustomTextProjectKind };

export const CustomTextProjectPage = observer((props: ProjectPageProps) => {
    const { projectId, projectKind } = props;

    const history = useHistory();
    const { localize } = useLocalization();
    const { rootStore, dispatcher } = useGlobalState();
    const { isLoaded, isFailed } = useInitializeCustomTextProject({ projectId, projectKind });

    useInstallHandlerGroup(
        dispatcher,
        createAzureOpenAiHandlerGroup,
        createCustomTextGptHandlerGroup,
        createCustomTextTrainingHandlerGroup,
        createAzureMachineLearningHandlerGroup,
        createCustomTextAutoTaggingHandlerGroup,
        createCustomTextDeployModelHandlerGroup
    );
    useModelTrainingTracker(rootStore.currentCustomTextProjectStore?.trainingJobStoreHouse, jobId => {
        history.push(routeTable.customText.trainingPage(projectId, projectKind), { selectedJobId: jobId });
        dispatcher.dispatch('hideFloatingToast', { id: jobId });
    });
    useModelEvaluationTracker(rootStore.currentCustomTextProjectStore?.evaluationJobStore, trainedModelName => {
        history.push(routeTable.customText.modelDetailsPage(projectId, projectKind, trainedModelName));
    });

    useAutoTaggingJobTracker();
    useModelTrainingUpdate({
        onUpdateTrainedModels: () => {
            rootStore.currentCustomTextProjectStore.trainedModels.setItems([]);
            dispatcher.dispatch('getCustomTextTrainedModels', []);
        },
        currentProjectStore: rootStore.currentCustomTextProjectStore
    });

    if (isFailed) {
        return projectKind === 'CustomEntityRecognition' ? (
            <Redirect to={routeTable.customText.extractionProjectsPage()} />
        ) : (
            <Redirect to={routeTable.customText.classificationProjectsPage()} />
        );
    }

    return isLoaded ? (
        <Switch>
            <PrivateRoute path={routeTable.customText.dataTaggingPage(projectId, projectKind)} component={CustomTextDataTaggingPage} />
            {(projectKind === 'CustomEntityRecognition' || FeatureToggles.CustomTextGptLabeling) && (
                <PrivateRoute path={routeTable.customText.autoTaggingPage(projectId, projectKind)} component={CustomTextAutoTaggingPage} />
            )}
            <PrivateRoute path={routeTable.customText.trainingPage(projectId, projectKind)} component={CustomTextTrainingPage} />
            <PrivateRoute
                path={routeTable.customText.modelDetailsListPage(projectId, projectKind)}
                exact
                component={CustomTextModelDetailsListPage}
            />
            <PrivateRoute path={routeTable.customText.modelDetailsPage(projectId, projectKind)} component={CustomTextModelDetailsPage} />
            <PrivateRoute path={routeTable.customText.testModelsPage(projectId, projectKind)} exact component={CustomTextTestModelPage} />
            <PrivateRoute path={routeTable.customText.deployModelPage(projectId, projectKind)} component={CustomTextDeployModelPage} />
            <PrivateRoute path={routeTable.customText.projectSettingsPage(projectId, projectKind)} component={CustomTextSettingsPage} />

            <Redirect
                from={routeTable.customText.projectPage(projectId, projectKind)}
                to={routeTable.customText.dataTaggingPage(projectId, projectKind)}
            />
            <NotFoundRedirect />
        </Switch>
    ) : (
        <Page
            title={localize(globalMessages.loading)}
            pageName="CustomTextProjectPage"
            size="container-fluid"
            animateIn={false}
            data-id="CustomTextProjectPage">
            <ShimmerFactory fullWidth includeHeaderShimmer shimmerCount={10} />
        </Page>
    );
});
